import React, { useEffect, useMemo, useRef, useState } from "react";
import PaymentOption from "./PaymentOption";
import PaymentMethod from "./PaymentMethod";
import { useNavigate } from "react-router-dom";
import {
  useAppDispatch,
  useAppSelector,
  PaymentMethodObjType,
  API_PAYMENT_METHODS,
  CURRENCY_TYPES,
  PAYMENT_METHODS,
  setWithdrawPaymentConfig,
  WithdrawPaymentConfig,
  createNewAccount,
  deleteAccount,
  getPaymentConfig,
  getPaymentProviders,
  withdrawFunds,
  reqWithdrawCryptoPgmanFunds,
  AddAccountFormType,
  AddAccountPayloadType,
  AddCryptoWalletFormType,
  AddCryptoWalletPayloadType,
  AddUpiAccountFormType,
  AddUpiAccountPayloadType,
  DeleteAccountParamsType,
  WithdrawFormType,
  cryptoCurrencyList,
  getAddAccountFromInitialState,
  getAddCryptoWalletInitialForm,
  getAddUpiAccountInitialForm,
  getInitialWithdrawForm,
  HOME_ROUTE,
  MY_TRANSACTION_ROUTE,
  showToast,
  getFieldFromToken,
  JwtToken,
  logout,
  paymentSettingsMethodsUrl,
  parsePaymentSettingsResponse,
  providerExchangeRatesUrl,
  getOtpButtonVisibility,
} from "@hypexone/uicore";
import { findCryptoExchangeRate } from "../../../utils/findCryptoExchangeRate";

import { AddAccountFormFields, WithdrawFormFields } from "./WithdrawPageForms";
import { AGPAY_API_CLIENT, SVLS_API_CLIENT } from "../../../App.utils";
import { normalizeInput } from "../Payments/commonutils";

const initialAddAccountFormState = getAddAccountFromInitialState();
const initialWithdrawForm = getInitialWithdrawForm();
const initialAddUpiAccountState = getAddUpiAccountInitialForm();
const initialAddCryptoWalletState = getAddCryptoWalletInitialForm();

const getWithdrawProviders = (
  config: WithdrawPaymentConfig | undefined,
  method?: string
): string[] | undefined => {
  if (!config || !method) return undefined;
  return (
    config[method] ??
    (method === PAYMENT_METHODS.UPI ? config.UPI : undefined) ??
    (method === "UPI" ? config[PAYMENT_METHODS.UPI] : undefined)
  );
};

const isBankTransferMethod = (method?: string) =>
  method === PAYMENT_METHODS.BANK_TRANSFER || method === "BANK_TRANSFER";

const isUpiMethod = (method?: string) =>
  method === PAYMENT_METHODS.UPI ||
  method === "UPI" ||
  method === API_PAYMENT_METHODS.UPI;

const isCryptoMethod = (method?: string) =>
  method === PAYMENT_METHODS.CRYPTO_WALLET_TRANSFER ||
  method === "CRYPTO_WALLET_TRANSFER" ||
  method === API_PAYMENT_METHODS.CRYPTO_WALLET_TRANSFER;

const getApiPaymentOption = (method: string): string => {
  if (isCryptoMethod(method)) {
    return "CRYPTO_WALLET";
  }
  if (isUpiMethod(method)) {
    return "UPI";
  }
  if (isBankTransferMethod(method)) {
    return "NEFT";
  }
  return method;
};

interface WithdrawAmountProps {}

const WithdrawAmount: React.FC<WithdrawAmountProps> = () => {
  const { withdrawPaymentConfig, isLoading: paymentConfigLoading } =
    useAppSelector((state) => state.payment);
  const { paymentMethodList } = useAppSelector((state) => state.payment);
  const { domainConfig } = useAppSelector((state) => state.common);
  const { langData } = useAppSelector((state) => state.cms);
  const [loading, setLoading] = useState<boolean>(false);
  const navigate = useNavigate();
  const [currentSelectedAccount, setCurrentSelectedAccount] =
    useState<PaymentMethodObjType>();

  const dispatch = useAppDispatch();
  const { balanceSummary } = useAppSelector((state) => state.auth);
  const cashableAmount = Number(balanceSummary?.cashableAmount ?? 0);

  const paymentConfig = useMemo(
    () => withdrawPaymentConfig ?? {},
    [withdrawPaymentConfig]
  );

  const getPaymentMethods = (): PAYMENT_METHODS[] => {
    const keys = Object.keys(paymentConfig);
    return keys.length > 0 ? (keys as PAYMENT_METHODS[]) : [];
  };

  const [showAddAccountForm, setShowAddAccountForm] = useState(false);

  const [selectedPaymentProvider, setSelectedPaymentProvider] = useState("");
  const [receivableAmount, setReceivableAmount] = useState<number>(null);
  const [cryptoAmount, setCryptoAmount] = useState<string>("");
  const [exchangeRate, setExchangeRate] = useState<number | null>(null);
  const [withdrawCryptoCurrencies, setWithdrawCryptoCurrencies] = useState<any[]>([]);
  const [minTxnAmount, setMinTxnAmount] = useState<number>(100);

  const [selectedPaymentMethod, setSelectedPaymentMethod] =
    useState<PAYMENT_METHODS>();

  const selectedMethodProviders = getWithdrawProviders(
    paymentConfig,
    selectedPaymentMethod
  );

  useEffect(() => {
    dispatch(getPaymentConfig());

    const fetchPaymentSettings = async () => {
      try {
        const response = await AGPAY_API_CLIENT.get(
          paymentSettingsMethodsUrl(),
          {
            headers: {
              Authorization: sessionStorage.getItem("jwt_token"),
            },
          }
        );
        if (response.status === 200) {
          const settings = parsePaymentSettingsResponse(response.data);
          const withdrawMethods = settings.withdrawMethods;
          if (Object.keys(withdrawMethods).length > 0) {
            dispatch(setWithdrawPaymentConfig(withdrawMethods));
          }
          setWithdrawCryptoCurrencies(settings.withdraw_crypto_currencies);
          setMinTxnAmount(settings.minTxnAmount ?? 0);
        }
      } catch (err) {
        console.log("Error fetching payment settings:", err);
      }
    };
    fetchPaymentSettings();
  }, [dispatch]);

  useEffect(() => {
    const paymentMethodConfig = Object.keys(paymentConfig);
    if (paymentMethodConfig.length === 0) return;

    const firstMethod = paymentMethodConfig[0] as PAYMENT_METHODS;
    const providers = getWithdrawProviders(paymentConfig, firstMethod);
    if (!providers?.length) return;

    setSelectedPaymentMethod((current) => current ?? firstMethod);
    setSelectedPaymentProvider((current) => current ?? providers[0]);
  }, [paymentConfig]);

  useEffect(() => {
    if (!selectedPaymentMethod) return;

    const providers = getWithdrawProviders(paymentConfig, selectedPaymentMethod);
    if (providers?.length) {
      setSelectedPaymentProvider(providers[0]);
      dispatch(
        getPaymentProviders(
          getApiPaymentOption(selectedPaymentMethod) as API_PAYMENT_METHODS
        )
      );
    }

    setCurrentSelectedAccount(null);
    resetForms();
    setShowAddAccountForm(false);
  }, [selectedPaymentMethod, paymentConfig, dispatch]);

  const [withdrawFormData, setWithdrawFormData] =
    useState<WithdrawFormType>(initialWithdrawForm);

  const [addAccountFormData, setAddAccountFormData] =
    useState<AddAccountFormType>(initialAddAccountFormState);

  const [addUpiAccountFormData, setAddUpiAccountFormData] =
    useState<AddUpiAccountFormType>({ ...initialAddUpiAccountState, otp: "" });

  const [addCryptoWalletFormData, setAddCryptoWalletFormData] =
    useState<AddCryptoWalletFormType>(initialAddCryptoWalletState);

  const [otpTimer, setOtpTimer] = useState<number>();
  const [phoneNumbeErrorMsg, setPhoneNumbeErrorMsg] = useState<string>('');
  const [otpLoader, setOtpLoader] = useState<boolean>(false);
  const otpTimerIdRef = useRef(null);

  const handleOtpTimer = (time) => {
    if (otpTimerIdRef.current) {
      clearTimeout(otpTimerIdRef.current);
    }

    if (time === 0) {
      setOtpTimer(undefined);
      return;
    }

    if (time > 0) {
      setOtpTimer(time);
      otpTimerIdRef.current = setTimeout(() => {
        handleOtpTimer(time - 1);
      }, 1000);
    }
  };

  const stopTimer = () => {
    if (otpTimerIdRef.current) {
      clearTimeout(otpTimerIdRef.current);
      otpTimerIdRef.current = null;
    }
    setOtpTimer(0);
  };

  useEffect(() => {
    let mode = getFieldFromToken(JwtToken.MODE);
    if(!mode) {
      dispatch(logout())
    }
  },[])

  const isOnlineUser = getFieldFromToken(JwtToken.MODE)?.toLowerCase() === 'online';

  const sendOtp = async (isWhatsapp = false) => {
    setPhoneNumbeErrorMsg('');
    setOtpLoader(true);
    try {
      const url = isWhatsapp
        ? `/account/v2/users/otp?isWhatsapp=true`
        : `/account/v2/users/otp`;
      await SVLS_API_CLIENT.post(
        url,
        {},
        {
          headers: {
            Authorization: sessionStorage.getItem('jwt_token'),
          },
        }
      );
      handleOtpTimer(60);
    } catch (err) {
      setPhoneNumbeErrorMsg(err?.response?.data?.message);
    }
    setOtpLoader(false);
  };

  const { showSmsOtpButton, showWhatsappOtpButton, showGenericOtpButton } =
    getOtpButtonVisibility(domainConfig);

  const handleWithdrawFormChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    withdrawFormData[event.target.id] = event.target.value;
    setWithdrawFormData({ ...withdrawFormData });
  };

  const handleAddAccountFormChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    addAccountFormData[event.target.id] = event.target.value;
    setAddAccountFormData({ ...addAccountFormData });
  };

  const handleAddUpiAccountFormChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    addUpiAccountFormData[event.target.id] = event.target.value;
    setAddUpiAccountFormData({ ...addUpiAccountFormData });
  };

  const resetForms = () => {
    // TODO: Make this work with initialFormData from utils
    stopTimer();
    setAddAccountFormData({
      accountHolderName: "",
      accountNumber: "",
      bankName: "",
      branchName: "",
      ifscCode: "",
      displayName: "",
      otp: "",
    });
    setWithdrawFormData({ amount: "", notes: "" });
    setAddCryptoWalletFormData({
      selectedCryptoCurrency: null,
      walletAddress: "",
      otp: "",
    });
    setAddUpiAccountFormData({
      upiId: "",
      accountHolderName: "",
      displayName: "",
      otp: "",
    });
  };

  const isCryptoPayment = currentSelectedAccount?.paymentMethod === "CRYPTO_WALLET_TRANSFER";
  const isWithdrawSaveBtnDisabled = isCryptoPayment
    ? !(cryptoAmount && Number(cryptoAmount) > 0 && withdrawFormData.notes)
    : !(withdrawFormData.amount && withdrawFormData.notes);

  const onPaymentMethodClick = (paymentMethod: PAYMENT_METHODS) => {
    setSelectedPaymentMethod(paymentMethod);
    setReceivableAmount(null);
  };

  const checkAddAccountSaveBtnEnabledStatus = () => {
    if (isBankTransferMethod(selectedPaymentMethod)) {
      return !(
        addAccountFormData.accountNumber &&
        addAccountFormData.accountHolderName &&
        addAccountFormData.displayName &&
        addAccountFormData.bankName &&
        addAccountFormData.branchName &&
        addAccountFormData.ifscCode
      );
    } else if (isUpiMethod(selectedPaymentMethod)) {
      return !(
        addUpiAccountFormData.accountHolderName &&
        addUpiAccountFormData.displayName &&
        addUpiAccountFormData.upiId
      );
    } else {
      return !(
        addCryptoWalletFormData.selectedCryptoCurrency &&
        addCryptoWalletFormData.walletAddress
      );
    }
  };

  const isAddAccountSaveBtnDisabled = checkAddAccountSaveBtnEnabledStatus();

  const onSuccessCallback = () => {
    resetForms();
    navigate(MY_TRANSACTION_ROUTE);
  };

  const getReceivableAmount = async () => {
    const isCryptoPayment = currentSelectedAccount?.paymentMethod === "CRYPTO_WALLET_TRANSFER";
    const amountToUse = isCryptoPayment && cryptoAmount
      ? Number(cryptoAmount) * (exchangeRate || 1)
      : withdrawFormData.amount;

    if (!amountToUse || Number(amountToUse) <= 0) {
      dispatch(
        showToast({
          type: "error",
          message: isCryptoPayment
            ? langData?.["enter_crypto_amount"] || "Please enter crypto amount"
            : langData?.["enter_amount"],
        })
      );
      return false;
    }
    try {
      const payload = {
        amount: +amountToUse,
      };
      setLoading(true);
      let paymentProvider = selectedPaymentProvider?.toLowerCase() === "payzlncrypto"
        ? "payzln-crypto"
        : selectedPaymentProvider?.toLowerCase() === "payzlncryptoseamless" ? "payzln-crypto-seamless" : selectedPaymentProvider;
      const response = await AGPAY_API_CLIENT.post(
        providerExchangeRatesUrl(paymentProvider),
        payload,
        {
          headers: {
            Authorization: sessionStorage.getItem("jwt_token"),
          },
        }
      );
      if (response.status === 200) {
        const rates = Array.isArray(response?.data)
          ? response.data
          : (response?.data?.data ?? []);
        const matchingRate = findCryptoExchangeRate(rates, {
          cryptoCurrency: currentSelectedAccount.paymentMethodDetails.cryptoCurrency,
          blockchain: currentSelectedAccount.paymentMethodDetails.blockchain,
          networkId: currentSelectedAccount.paymentMethodDetails.networkId,
        });
        if (matchingRate) {
          setReceivableAmount(matchingRate?.receivable_amount);
        }
      }
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const handleWithdrawFormSubmission = (
    e: React.FormEvent<HTMLFormElement>
  ) => {
    e.preventDefault();
    // Use this handler to implement exch rates call for crypto
    handleWithdrawFunds(e);
  };

  const handleWithdrawFunds = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const isCryptoPayment = currentSelectedAccount?.paymentMethod === "CRYPTO_WALLET_TRANSFER";
    const isPgman = selectedPaymentProvider?.toLowerCase() === "pgman";
    const amountToValidate = isCryptoPayment && isPgman && cryptoAmount
      ? Number(cryptoAmount) * (exchangeRate || 1)
      : Number(withdrawFormData.amount);

    if (amountToValidate < 100) {
      dispatch(
        showToast({
          type: "error",
          message:
            (langData?.["min_withdrawal_amount_txt"] ||
              "Withdrawal Amount cannot be less than") +
            " " +
            100,
        })
      );
      return;
    }

    try {
      setLoading(true);
      const convertedCryptoAmount =
        cryptoAmount && Number(cryptoAmount) > 0
          ? (Number(cryptoAmount) * (exchangeRate || 1)).toString()
          : withdrawFormData.amount;
      let payload: any;
      if (isBankTransferMethod(selectedPaymentMethod)) {
        payload = {
          selectedPaymentProvider: selectedPaymentProvider,
          amount: withdrawFormData.amount,
          notes: withdrawFormData.notes,
          payment_method_id: currentSelectedAccount.id.toString(),
          payment_method: PAYMENT_METHODS.BANK_TRANSFER,
          currency_type: CURRENCY_TYPES.INR,
          mobile_number: "9876543211",
          onSuccessCallback: onSuccessCallback,
        };
      } else if (isUpiMethod(selectedPaymentMethod)) {
        payload = {
          selectedPaymentProvider: selectedPaymentProvider,
          amount: withdrawFormData.amount,
          notes: withdrawFormData.notes,
          payment_method_id: currentSelectedAccount.id.toString(),
          currency_type: CURRENCY_TYPES.INR,
          onSuccessCallback: onSuccessCallback,
        };
      } else {
        const isPgmanCrypto =
          selectedPaymentProvider?.toLowerCase() === "pgman" && isCryptoPayment;

        if (isPgmanCrypto) {
          const pmDetails: any = currentSelectedAccount?.paymentMethodDetails || {};
          const cryptoCurrency =
            pmDetails.crypto_currency ||
            pmDetails.cryptoCurrency ||
            pmDetails.currency ||
            "";
          const networkId = (
            pmDetails.network_id ||
            pmDetails.networkId ||
            ""
          ).toString();
          const walletAddress =
            pmDetails.wallet_address ||
            pmDetails.walletAddress ||
            pmDetails.address ||
            "";

          await reqWithdrawCryptoPgmanFunds(AGPAY_API_CLIENT, {
            selectedPaymentProvider: "pgman",
            crypto_amount: cryptoAmount,
            crypto_currency: cryptoCurrency,
            network_id: networkId,
            payment_method: PAYMENT_METHODS.CRYPTO_WALLET_TRANSFER,
            payment_method_id: currentSelectedAccount.id.toString(),
            wallet_address: walletAddress,
          });
          dispatch(
            showToast({ message: "Transaction Saved Successfully!", type: "success" })
          );
          onSuccessCallback();
          return;
        }

        payload = {
          selectedPaymentProvider:
            selectedPaymentProvider?.toLowerCase() === "payzlncrypto"
              ? "payzln-crypto"
              : selectedPaymentProvider?.toLowerCase() === "payzlncryptoseamless"
              ? "payzln-crypto-seamless"
              : selectedPaymentProvider,
          amount: isCryptoPayment ? convertedCryptoAmount : withdrawFormData.amount,
          notes: withdrawFormData.notes,
          payment_method_id: currentSelectedAccount.id.toString(),
          payment_method: PAYMENT_METHODS.CRYPTO_WALLET_TRANSFER,
          crypto_amount: cryptoAmount || undefined,
          crypto_currency:
            currentSelectedAccount?.paymentMethodDetails?.cryptoCurrency,
          network_id: (
            currentSelectedAccount?.paymentMethodDetails?.networkId || ""
          ).toString(),
          wallet_address:
            currentSelectedAccount?.paymentMethodDetails?.walletAddress,
          onSuccessCallback: onSuccessCallback,
          currency_type: CURRENCY_TYPES.INR,
        };
      }

      await dispatch(withdrawFunds(payload)).unwrap();
    } catch (err) {
      console.log("Error: ", err);
    } finally {
      setLoading(false);
    }
  };

  const addAccountSuccess = () => {
    if (!selectedPaymentMethod) return;
    dispatch(
      getPaymentProviders(
        getApiPaymentOption(selectedPaymentMethod) as API_PAYMENT_METHODS
      )
    );
    resetForms();
  };

  const onAddAccountSaveBtnClick = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    try {
      setLoading(true);
      let payload:
        | AddUpiAccountPayloadType
        | AddAccountPayloadType
        | AddCryptoWalletPayloadType = null;
      if (isBankTransferMethod(selectedPaymentMethod)) {
        if (isOnlineUser && !addAccountFormData.otp || addAccountFormData.otp.length > 6) {
          dispatch(
            showToast({
              message: "Please enter valid OTP",
              type: "error",
            }))
          return;
        }
        payload = {
          accountHolderName: normalizeInput(addAccountFormData.accountHolderName),
          accountNumber: normalizeInput(addAccountFormData.accountNumber),
          bankName: normalizeInput(addAccountFormData.bankName),
          branchName: normalizeInput(addAccountFormData.branchName),
          displayName: normalizeInput(addAccountFormData.displayName),
          ifscCode: normalizeInput(addAccountFormData.ifscCode),
          paymentMethod: PAYMENT_METHODS.BANK_TRANSFER,
          otp: addAccountFormData.otp,
          onSuccessCallback: addAccountSuccess,
        };
      }
      else if (isUpiMethod(selectedPaymentMethod)) {
        if (isOnlineUser && !addUpiAccountFormData.otp || addUpiAccountFormData.otp.length > 6) {
          dispatch(
            showToast({
              message: "Please enter valid OTP",
              type: "error",
            }))
          return;
        }
        payload = {
          accountHolderName: normalizeInput(addUpiAccountFormData.accountHolderName),
          accountNumber: normalizeInput(addUpiAccountFormData.upiId),
          displayName: normalizeInput(addUpiAccountFormData.displayName),
          ifscCode: "",
          paymentMethod: PAYMENT_METHODS.UPI,
          otp: addUpiAccountFormData.otp,
          onSuccessCallback: addAccountSuccess,
        };
      } else {
        if (isOnlineUser && !addCryptoWalletFormData.otp || addCryptoWalletFormData.otp.length > 6) {
          dispatch(
            showToast({
              message: "Please enter valid OTP",
              type: "error",
            }))
          return;
        }
        const {
          selectedCryptoCurrency: { blockchain, crypto_currency, network_id },
        } = addCryptoWalletFormData;
        payload = {
          paymentMethod: PAYMENT_METHODS.CRYPTO_WALLET_TRANSFER,
          accountNumber: normalizeInput(addCryptoWalletFormData.walletAddress),
          cryptoCurrencyType: crypto_currency,
          blockchain: blockchain,
          networkId: network_id,
          otp: addCryptoWalletFormData.otp,
          onSuccessCallback: addAccountSuccess,
        };
      }
      const { onSuccessCallback: _, ...payloadForApi } = payload as any;

      dispatch(
        createNewAccount({
          addAccountPayload: payloadForApi,
          onSuccessCallback: payload.onSuccessCallback,
        })
      );
    } catch (err) {
      console.log(err);
    } finally {
      setLoading(false);
    }
  };

  const onSavedAccountCardClick = (account: PaymentMethodObjType) => {
    setShowAddAccountForm(false);
    setCurrentSelectedAccount(account);
    setReceivableAmount(null);
    setCryptoAmount("");
    setExchangeRate(null);
    setWithdrawFormData({
      amount: "",
      notes: ""
    });
    if (account?.paymentMethod === "CRYPTO_WALLET_TRANSFER") {
      fetchCryptoCurrenciesForWithdraw(account);
    }
  };

  const fetchCryptoCurrenciesForWithdraw = async (account: PaymentMethodObjType) => {
    if (account?.paymentMethod === "CRYPTO_WALLET_TRANSFER" && account?.paymentMethodDetails) {
      try {
        const response = await AGPAY_API_CLIENT.get(
          paymentSettingsMethodsUrl(),
          {
            headers: {
              Authorization: sessionStorage.getItem("jwt_token"),
            },
          }
        );
        if (response.status === 200) {
          const settings = parsePaymentSettingsResponse(response.data);
          const currencies = settings.withdraw_crypto_currencies;
          const accountNetworkId = account.paymentMethodDetails.networkId;
          const accountCryptoCurrency = account.paymentMethodDetails.cryptoCurrency;
          const accountBlockchain = account.paymentMethodDetails.blockchain;

          const matchingCurrency = findCryptoExchangeRate(currencies, {
            cryptoCurrency: accountCryptoCurrency,
            blockchain: accountBlockchain,
            networkId: accountNetworkId,
          });

          if (matchingCurrency?.exchange_rate || matchingCurrency?.exchangeRate) {
            setExchangeRate(matchingCurrency.exchange_rate || matchingCurrency.exchangeRate);
          }
        }
      } catch (error) {
        console.log("error fetching crypto currencies:", error);
      }
    }
  };

  const onAddNewAccountPress = () => {
    setCurrentSelectedAccount(null);
    setShowAddAccountForm(true);
  };

  const onAccountDeleteSuccess = () => {
    if (!selectedPaymentMethod) return;
    dispatch(
      getPaymentProviders(
        getApiPaymentOption(selectedPaymentMethod) as API_PAYMENT_METHODS
      )
    );
    setCurrentSelectedAccount(null);
  };

  const onAccountDeleteClick = (account: PaymentMethodObjType) => {
    const deleteAccountParams: DeleteAccountParamsType = {
      paymentMethodId: account.id.toString(),
      onSuccessCallback: onAccountDeleteSuccess,
    };
    dispatch(deleteAccount(deleteAccountParams));
  };

  const handlePaymentProviderSelect = (
    e: React.ChangeEvent<HTMLSelectElement>
  ) => {
    setSelectedPaymentProvider(e.target.value);
  };

  useEffect(() => {
    if (!domainConfig.payments) {
      navigate(HOME_ROUTE, { replace: true });
    }
  }, []);

  //TODO: Create a custom hook for all above functionalities and separate the logic and UI

  return (
    <section className="flex flex-col items-start pb-44 bg-white rounded border border-solid border-black border-opacity-10 max-md:pb-24 md:p-0">
      <header className="self-stretch px-2 py-1.5 text-lg max-md:text-[18px] text-white rounded bg-header-gradient max-md:pr-5 max-md:max-w-full">
        <div className="px-2 py-2 rounded-md border border-[#ffb80c] text-lg font-bold tracking-wide uppercase">
          {langData?.["withdraw_amount"] || "WITHDRAW AMOUNT"}
        </div>
        <div className="flex flex-wrap items-center justify-between gap-1 px-1 py-1 text-sm sm:text-base">
          <span>
            {langData?.["following_payment_withdrawal_information"] ||
              "Following Payment Withdrawal Information::"}
          </span>
          <span className="whitespace-nowrap">
            {langData?.["cashable_amount"] || "Cashable Amount"} : {Math.floor(cashableAmount)}
          </span>
        </div>
      </header>
      {paymentConfigLoading && (
        <p className="mt-4 md:ml-4 text-base text-black">
          {langData?.["loading"] ?? "Loading..."}
        </p>
      )}
      {!paymentConfigLoading &&
        selectedMethodProviders &&
        selectedMethodProviders.length > 0 && (
          <>
            <div className="flex flex-col mt-4 md:ml-4 max-w-full w-full">
              <PaymentMethod
                methods={getPaymentMethods()}
                onPaymentMethodClick={onPaymentMethodClick}
                selectedPaymentMethod={selectedPaymentMethod}
              />

              <label htmlFor={"provider-select"} className="self-start mt-4">
                {langData?.["select_provider"]}
              </label>

              <select
                id="provider-select"
                className="block w-full md:w-[40%] p-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-goldenYellow mt-2"
                onChange={handlePaymentProviderSelect}
                defaultValue={selectedMethodProviders[0]}
              >
                {selectedMethodProviders.map(
                    (option, index) => {
                      return (
                        <option key={index} value={option}>
                          {`${langData?.["option"]} ${index + 1}`}
                        </option>
                      );
                    }
                  )}
              </select>

              <p className="text-base text-black max-md:max-w-full mt-2">
                {langData?.["select_payment_option"]}:
              </p>
              <div className="flex items-center">
                {paymentMethodList && paymentMethodList.length > 0 ? (
                  <div className="flex gap-3 items-center mt-3 max-w-[75%] overflow-scroll p-2">
                    {paymentMethodList.map((method, index) => (
                      <PaymentOption
                        key={index}
                        paymentMethod={method}
                        onAccountDeleteClick={onAccountDeleteClick}
                        onSavedAccountCardClick={onSavedAccountCardClick}
                        currentSelectedAccount={currentSelectedAccount}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex gap-3 items-center mt-3 max-w-[75%] justify-center">
                    {langData?.["no_accounts_found"]}
                  </div>
                )}

                <div
                  onClick={onAddNewAccountPress}
                  className="flex flex-col rounded-xl border border-dashed border-stone-300 justify-center items-center w-32 h-40 cursor-pointer"
                >
                  <div className="bg-zinc-100 w-8 h-8 flex items-center justify-center rounded-full text-lg">
                    +
                  </div>
                  <div className="mt-2.5 text-xs md:text-sm text-black">
                    {langData?.["add_new_account"]}
                  </div>
                </div>
              </div>
            </div>

            {/* Forms start here */}
            {showAddAccountForm ? (
              <AddAccountFormFields
                addAccountFormData={addAccountFormData}
                addUpiAccountFormData={addUpiAccountFormData}
                handleAddAccountFormChange={handleAddAccountFormChange}
                handleAddUpiAccountFormChange={handleAddUpiAccountFormChange}
                isSaveBtnDisabled={isAddAccountSaveBtnDisabled}
                onSave={onAddAccountSaveBtnClick}
                resetForms={resetForms}
                selectedPaymentMethod={selectedPaymentMethod}
                addCryptoWalletFormData={addCryptoWalletFormData}
                setAddCryptoWalletFormData={setAddCryptoWalletFormData}
                loading={loading}
                otpLoader={otpLoader}
                sendOtp={sendOtp}
                otpTimer={otpTimer}
                phoneNumbeErrorMsg={phoneNumbeErrorMsg}
                showSmsOtpButton={showSmsOtpButton || showGenericOtpButton}
                showWhatsappOtpButton={showWhatsappOtpButton}
                selectedPaymentProvider={selectedPaymentProvider}
                withdrawCryptoCurrencies={withdrawCryptoCurrencies}
              />
            ) : (
              <>
                {currentSelectedAccount && (
                  <WithdrawFormFields
                    handleWithdrawFormChange={handleWithdrawFormChange}
                    withdrawFormData={withdrawFormData}
                    isWithdrawSaveBtnDisabled={isWithdrawSaveBtnDisabled}
                    loading={loading}
                    onSave={handleWithdrawFormSubmission}
                    getReceivableAmount={getReceivableAmount}
                    receivableAmount={receivableAmount}
                    currentSelectedAccount={currentSelectedAccount}
                    paymentProvider={selectedPaymentProvider}
                    cryptoAmount={cryptoAmount}
                    setCryptoAmount={setCryptoAmount}
                    exchangeRate={exchangeRate}
                    minTxnAmount={minTxnAmount}
                  />
                )}
              </>
            )}
          </>
        )}
      {!paymentConfigLoading &&
        (!selectedMethodProviders || selectedMethodProviders.length === 0) && (
          <p className="mt-4 md:ml-4 text-base text-black">
            {langData?.["no_payment_methods_available"] ??
              "No withdrawal methods are available at the moment."}
          </p>
        )}
    </section>
  );
};

export default WithdrawAmount;
